import AxiosConfig from '../utils/AxiosConfig';

class B2BProformaService {
  // Obtener todas las proformas B2B con paginado
  async getProformas(page = 1, pageSize = 10, searchTerm = '', status = '', options = {}) {
    try {
      const {
        sortField,
        sortDirection,
        filterField,
        filterOperator,
        filterValue,
        destinationLocationId
      } = options;

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });
      
      if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      if (status && typeof status === 'string' && status.trim()) {
        params.append('status', status.trim());
      }
      if (sortField) params.append('sortField', sortField);
      if (sortDirection) params.append('sortDirection', sortDirection);
      if (filterField) params.append('filterField', filterField);
      if (filterOperator) params.append('filterOperator', filterOperator);
      if (filterValue !== undefined && filterValue !== null && String(filterValue).trim() !== '') {
        params.append('filterValue', String(filterValue).trim());
      }
      if (destinationLocationId) {
        params.append('destinationLocationId', String(destinationLocationId));
      }
      
      const response = await AxiosConfig.get(`/b2b/proformas?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching B2B proformas:', error);
      throw error;
    }
  }

  // Obtener una proforma específica por ID
  async getProformaById(proformaId) {
    try {
      const response = await AxiosConfig.get(`/b2b/proformas/${proformaId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching B2B proforma:', error);
      throw error;
    }
  }

  // Crear una nueva proforma B2B
  async createProforma(proformaData) {
    try {
      const response = await AxiosConfig.post('/b2b/proformas', proformaData);
      return response.data;
    } catch (error) {
      console.error('Error creating B2B proforma:', error);
      throw error;
    }
  }

  async createFranchiseRequest(requestData) {
    try {
      const response = await AxiosConfig.post('/b2b/proformas/franchise-request', requestData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error creating B2B franchise proforma request:', error);
      throw error;
    }
  }

  async getFranchiseRequestProducts(destinationLocationId, branchId = null) {
    try {
      const params = new URLSearchParams({
        destinationLocationId: String(destinationLocationId)
      });
      if (branchId) {
        params.append('branchId', String(branchId));
      }

      const response = await AxiosConfig.get(`/b2b/proformas/franchise-products?${params}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching B2B franchise request products:', error);
      throw error;
    }
  }

  // Actualizar una proforma B2B
  async updateProforma(proformaId, proformaData) {
    try {
      const response = await AxiosConfig.put(`/b2b/proformas/${proformaId}`, proformaData);
      return response.data;
    } catch (error) {
      console.error('Error updating B2B proforma:', error);
      throw error;
    }
  }

  // Cambiar estado de una proforma B2B
  async updateProformaStatus(proformaId, status) {
    try {
      const response = await AxiosConfig.put(`/b2b/proformas/${proformaId}/status`, {
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error updating proforma status:', error);
      throw error;
    }
  }

  // Eliminar una proforma B2B
  async deleteProforma(proformaId) {
    try {
      const response = await AxiosConfig.delete(`/b2b/proformas/${proformaId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting B2B proforma:', error);
      throw error;
    }
  }

  // Aceptar proforma y crear pedido
  // options: { changeNote, notifyClient } -- nota para el cliente y si se le avisa por email
  async acceptProforma(proformaId, options = {}) {
    try {
      const response = await AxiosConfig.post(`/b2b/proformas/${proformaId}/accept`, options);
      return response.data;
    } catch (error) {
      console.error('Error accepting proforma:', error);
      throw error;
    }
  }

  // Rechazar proforma
  async rejectProforma(proformaId, reason) {
    try {
      const response = await AxiosConfig.post(`/b2b/proformas/${proformaId}/reject`, {
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error rejecting proforma:', error);
      throw error;
    }
  }


  // Generar PDF de proforma
  async generateProformaPDF(proformaId) {
    try {
      const response = await AxiosConfig.get(`/b2b/proformas/${proformaId}/pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error generating proforma PDF:', error);
      throw error;
    }
  }

  // Obtener proformas por cliente
  async getProformasByClient(clientId) {
    try {
      const response = await AxiosConfig.get(`/b2b/clients/${clientId}/proformas`);
      return response.data;
    } catch (error) {
      console.error('Error fetching proformas by client:', error);
      throw error;
    }
  }

  // Obtener proformas por estado
  async getProformasByStatus(status) {
    try {
      const response = await AxiosConfig.get(`/b2b/proformas/status/${status}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching proformas by status:', error);
      throw error;
    }
  }

  // Obtener proformas vencidas
  async getExpiredProformas() {
    try {
      const response = await AxiosConfig.get('/b2b/proformas/expired');
      return response.data;
    } catch (error) {
      console.error('Error fetching expired proformas:', error);
      throw error;
    }
  }

  // Validar datos de proforma
  validateProformaData(proformaData) {
    const errors = {};

    if (!proformaData.clientId) {
      errors.clientId = 'El cliente es requerido';
    }

    if (!proformaData.items || proformaData.items.length === 0) {
      errors.items = 'Debe agregar al menos un producto';
    }

    if (proformaData.validityDays !== undefined && proformaData.validityDays < 0) {
      errors.validityDays = 'Los días de validez deben ser mayores o iguales a 0';
    }

    // Validar items
    if (proformaData.items) {
      proformaData.items.forEach((item, index) => {
        if (!item.productId) {
          errors[`items[${index}].productId`] = 'El producto es requerido';
        }
        if (!item.quantity || item.quantity <= 0) {
          errors[`items[${index}].quantity`] = 'La cantidad debe ser mayor a 0';
        }
        if (!item.unitPrice || item.unitPrice <= 0) {
          errors[`items[${index}].unitPrice`] = 'El precio unitario debe ser mayor a 0';
        }
      });
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Formatear datos de proforma para envío
  formatProformaDataForAPI(proformaData) {
    return {
      clientId: proformaData.clientId,
      status: proformaData.status || 'sent',
      notes: proformaData.notes?.trim() || null,
      validityDays: proformaData.validityDays || 30,
      items: proformaData.items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discountType: item.discountType || 'percentage',
        discountValue: item.discountValue || 0,
        totalPrice: item.totalPrice
      }))
    };
  }

  // Calcular total de proforma
  calculateProformaTotal(items) {
    return items.reduce((total, item) => {
      return total + (item.totalPrice || 0);
    }, 0);
  }

  // Verificar si proforma está vencida
  isProformaExpired(proforma) {
    if (!proforma.validityDays || !proforma.createdAt) {
      return false;
    }

    const createdAt = new Date(proforma.createdAt);
    const expirationDate = new Date(createdAt.getTime() + (proforma.validityDays * 24 * 60 * 60 * 1000));
    
    return new Date() > expirationDate;
  }

  // Determinar estado de proforma
  determineProformaStatus(proforma) {
    if (proforma.status === 'accepted' || proforma.status === 'rejected') {
      return proforma.status;
    }

    if (this.isProformaExpired(proforma)) {
      return 'expired';
    }

    return proforma.status || 'sent';
  }

  // Obtener estados disponibles
  getProformaStatuses() {
    return [
      { value: 'sent', label: 'Enviado' },
      { value: 'accepted', label: 'Aceptada' },
      { value: 'rejected', label: 'Rechazada' },
      { value: 'expired', label: 'Vencida' }
    ];
  }

  // Obtener color del estado
  getStatusColor(status) {
    switch (status) {
      case 'sent':
        return 'info';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'expired':
        return 'warning';
      default:
        return 'default';
    }
  }

  // Obtener texto del estado
  getStatusText(status) {
    switch (status) {
      case 'sent':
        return 'Enviado';
      case 'accepted':
        return 'Aceptada';
      case 'rejected':
        return 'Rechazada';
      case 'expired':
        return 'Vencida';
      default:
        return 'Desconocido';
    }
  }

  // Enviar proforma por correo
  async sendProformaEmail(proformaId, email) {
    try {
      const response = await AxiosConfig.post(`/b2b/proformas/${proformaId}/send-email`, {
        email: email
      });
      return response.data;
    } catch (error) {
      console.error('Error sending proforma email:', error);
      throw error;
    }
  }

  // Generar número de proforma
  generateProformaNumber() {
    const timestamp = Date.now();
    return `PROF-${timestamp}`;
  }

  // Calcular fecha de vencimiento
  calculateExpirationDate(createdAt, validityDays) {
    if (!createdAt || !validityDays) return null;
    
    const date = new Date(createdAt);
    date.setDate(date.getDate() + validityDays);
    return date.toISOString();
  }
}

export default new B2BProformaService();
